import React, { useState, useEffect } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import type { AdminTransaction, TransactionFilter, TransactionCounts } from '../../services/adminApiService';
import './AdminComponents.css';
import { useAppSelector } from '../../store/hooks';
import TransactionDetailsModal from './TransactionDetailsModal';

type TransactionTab = 'all' | 'banking' | 'crypto' | 'trading' | 'mt5';

const TransactionManagement: React.FC = () => {
  const adminApiService = useAdminApiService();
  const [transactions, setTransactions] = useState<AdminTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0); // Change to 0-based indexing
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState<TransactionTab>('all');
  const [tabCounts, setTabCounts] = useState<TransactionCounts>({
    bankingCount: 0,
    cryptoCount: 0,
    tradingCount: 0,
    mt5Count: 0
  });
  const [filter, setFilter] = useState<TransactionFilter>({
    page: 0,
    size: 100,
    sortBy: 'transactionDate',
    sortDirection: 'DESC'
  });
  const [transactionDetailsModal, setTransactionDetailsModal] = useState<{
    isOpen: boolean;
    transaction: AdminTransaction | null;
  }>({
    isOpen: false,
    transaction: null
  });

  const adminToken = useAppSelector(state => state.auth.token) ?? undefined;

  useEffect(() => {
    console.log('TransactionManagement: useEffect triggered, loading transactions');
    loadTransactions();
  }, [filter]);

  useEffect(() => {
    if (transactions.length > 0) {
      // Calculate counts for each tab
      const counts: TransactionCounts = {
        bankingCount: transactions.filter(t => {
          const type = t.type?.toUpperCase() || '';
          return !type.startsWith('CRYPTO_') && !type.startsWith('STOCK_') && 
                 !type.startsWith('OPTION_') && !type.startsWith('MT5_');
        }).length,
        cryptoCount: transactions.filter(t => {
          const type = t.type?.toUpperCase() || '';
          return type.startsWith('CRYPTO_') || 
                 t.category?.toLowerCase().includes('crypto') ||
                 t.description?.toLowerCase().includes('crypto');
        }).length,
        tradingCount: transactions.filter(t => {
          const type = t.type?.toUpperCase() || '';
          return type.startsWith('STOCK_') || 
                 type.startsWith('OPTION_') ||
                 t.category?.toLowerCase().includes('trading') ||
                 t.category?.toLowerCase().includes('stock') ||
                 t.description?.toLowerCase().includes('trading') ||
                 t.description?.toLowerCase().includes('stock');
        }).length,
        mt5Count: transactions.filter(t => {
          const type = t.type?.toUpperCase() || '';
          return type.startsWith('MT5_') || 
                 t.category?.toLowerCase().includes('mt5') ||
                 t.description?.toLowerCase().includes('mt5');
        }).length
      };
      setTabCounts(counts);
    }
  }, [transactions]);

  // Force re-filtering when activeTab changes
  useEffect(() => {
    // This will trigger a re-render with the new filtered transactions
  }, [activeTab]);

  // Filter transactions based on active tab
  const getFilteredTransactions = () => {
    if (!transactions || transactions.length === 0) {
      return [];
    }

    // For 'all' tab, return all transactions
    if (activeTab === 'all') {
      return transactions;
    }

    // For other tabs, filter based on transaction type
    const filtered = transactions.filter(transaction => {
      const type = transaction.type?.toUpperCase() || '';
      
      switch (activeTab) {
        case 'banking':
          // Banking transactions: DEPOSIT, WITHDRAWAL, TRANSFER, PAYMENT, CREDIT, DEBIT
          // Exclude crypto, trading, and mt5 transactions
          const isBanking = ['DEPOSIT', 'WITHDRAWAL', 'TRANSFER', 'PAYMENT', 'CREDIT', 'DEBIT'].includes(type);
          return isBanking;
        case 'crypto':
          // Crypto transactions: CRYPTO_BUY, CRYPTO_SELL, CRYPTO_TRADE
          return type.startsWith('CRYPTO_');
        case 'trading':
          // Trading transactions: STOCK_BUY, STOCK_SELL, OPTION_BUY, OPTION_SELL
          return type.startsWith('STOCK_') || type.startsWith('OPTION_');
        case 'mt5':
          // MT5 transactions: MT5_DEPOSIT, MT5_WITHDRAWAL, MT5_TRADE
          return type.startsWith('MT5_');
        default:
          return true;
      }
    });

    return filtered;
  };

  const filteredTransactions = getFilteredTransactions();

  // Helper function to get count for a specific tab
  const getTabCount = (tab: TransactionTab): number => {
    switch (tab) {
      case 'all':
        return tabCounts.bankingCount + tabCounts.cryptoCount + tabCounts.tradingCount + tabCounts.mt5Count;
      case 'banking':
        return tabCounts.bankingCount;
      case 'crypto':
        return tabCounts.cryptoCount;
      case 'trading':
        return tabCounts.tradingCount;
      case 'mt5':
        return tabCounts.mt5Count;
      default:
        return 0;
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('TransactionManagement: Loading transactions with filter:', filter);
      
      // Add type to filter if we're on a specific tab
      const filterWithType = { ...filter };
      if (activeTab !== 'all') {
        switch (activeTab) {
          case 'banking':
            // Don't set type filter for banking - let server return all non-crypto/non-trading/non-mt5
            break;
          case 'crypto':
            // For crypto, we'll need to handle this differently since server doesn't support partial matching
            // We'll load all transactions and filter on client side for crypto only
            break;
          case 'trading':
            // Don't set type filter for trading - handle on client side
            break;
          case 'mt5':
            // Don't set type filter for mt5 - handle on client side
            break;
        }
      }

      console.log('TransactionManagement: Calling adminApiService.getTransactions with filter:', filterWithType);
      const response = await adminApiService.getTransactions(filterWithType);
      console.log('TransactionManagement: API response:', response);

      if (response.success && response.data) {
        setTransactions(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
        
        // Update total counts from response metadata
        if (response.data.totalElements) {
          const totalElements = response.data.totalElements;
          
          // Make a separate call to get category counts
          const countsResponse = await adminApiService.getTransactionCounts();
          if (countsResponse.success && countsResponse.data) {
            setTabCounts({
              bankingCount: countsResponse.data.bankingCount || 0,
              cryptoCount: countsResponse.data.cryptoCount || 0,
              tradingCount: countsResponse.data.tradingCount || 0,
              mt5Count: countsResponse.data.mt5Count || 0
            });
          }
        }
      } else {
        console.error('TransactionManagement: API call failed:', response);
        setError(response.error || 'Failed to load transactions');
      }
    } catch (err) {
      console.error('TransactionManagement: Exception during API call:', err);
      setError('Failed to load transactions: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTransaction = async (transactionId: number) => {
    if (!window.confirm('Are you sure you want to approve this transaction?')) {
      return;
    }

    try {
      const response = await adminApiService.approveTransaction(transactionId);
      if (response.success) {
        await loadTransactions();
      } else {
        alert(response.error || 'Failed to approve transaction');
      }
    } catch (err) {
      alert('Failed to approve transaction');
    }
  };

  const handleRejectTransaction = async (transactionId: number) => {
    const reason = prompt('Enter reason for rejection:');
    if (!reason) return;

    try {
      const response = await adminApiService.rejectTransaction(transactionId, reason);
      if (response.success) {
        await loadTransactions();
      } else {
        alert(response.error || 'Failed to reject transaction');
      }
    } catch (err) {
      alert('Failed to reject transaction');
    }
  };

  const openTransactionDetailsModal = (transaction: AdminTransaction) => {
    setTransactionDetailsModal({
      isOpen: true,
      transaction
    });
  };

  const closeTransactionDetailsModal = () => {
    setTransactionDetailsModal({
      isOpen: false,
      transaction: null
    });
  };

  const updateFilter = (updates: Partial<TransactionFilter>) => {
    setFilter(prev => ({ ...prev, ...updates, page: 1 }));
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    const sortDirection = filter.sortBy === field && filter.sortDirection === 'ASC' ? 'DESC' : 'ASC';
    updateFilter({ sortBy: field, sortDirection });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'No Date';
    let safeDateString = dateString.replace(/\.\d+/, '');
    if (!safeDateString.endsWith('Z')) {
      safeDateString += 'Z';
    }
    const date = new Date(safeDateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'status-badge approved';
      case 'PENDING': return 'status-badge pending';
      case 'FAILED': return 'status-badge rejected';
      case 'CANCELLED': return 'status-badge inactive';
      default: return 'status-badge';
    }
  };

  const getTransactionTypeIcon = (type: string) => {
    switch (type) {
      case 'DEPOSIT': return '⬇️';
      case 'WITHDRAWAL': return '⬆️';
      case 'TRANSFER': return '↔️';
      case 'PAYMENT': return '💳';
      case 'CREDIT': return '⬇️';
      case 'DEBIT': return '⬆️';
      case 'CRYPTO_BUY': return '₿';
      case 'CRYPTO_SELL': return '₿';
      case 'CRYPTO_TRADE': return '🔄';
      case 'STOCK_BUY': return '📈';
      case 'STOCK_SELL': return '📉';
      case 'MT5_DEPOSIT': return '⬇️🎯';
      case 'MT5_WITHDRAWAL': return '⬆️🎯';
      case 'MT5_TRADE': return '🎯';
      default: return '💰';
    }
  };

  const getTabInfo = (tab: TransactionTab) => {
    const tabInfo = {
      all: { label: 'All Transactions', icon: '💰', count: getTabCount('all') },
      banking: { label: 'Banking', icon: '🏦', count: getTabCount('banking') },
      crypto: { label: 'Crypto', icon: '₿', count: getTabCount('crypto') },
      trading: { label: 'Trading', icon: '📈', count: getTabCount('trading') },
      mt5: { label: 'MT5', icon: '🎯', count: getTabCount('mt5') }
    };
    return tabInfo[tab];
  };

  const handleTabClick = (tab: TransactionTab) => {
    setActiveTab(tab);
    setCurrentPage(0);
    setFilter(prev => ({
      ...prev,
      page: 0
      // Don't set type filter - we'll filter on client side
    }));
  };

  if (loading) {
    return (
      <div className="admin-component-loading">
        <div className="loading-spinner"></div>
        <p>Loading transactions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-component-error">
        <div className="error-icon">⚠️</div>
        <h3>Error Loading Transactions</h3>
        <p>{error}</p>
        <button className="retry-btn" onClick={loadTransactions}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="transaction-management">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h2>Transaction Management</h2>
          <p>Monitor, approve, and manage all system transactions</p>
        </div>
        <div className="dashboard-actions">
          <button className="refresh-btn" onClick={loadTransactions} disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Transaction Tabs */}
      <div className="transaction-tabs">
        {(['all', 'banking', 'crypto', 'trading', 'mt5'] as TransactionTab[]).map((tab) => {
          const tabInfo = getTabInfo(tab);
          return (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => handleTabClick(tab)}
            >
              <span className="tab-icon">{tabInfo.icon}</span>
              <span className="tab-label">{tabInfo.label}</span>
              <span className="tab-count">({tabInfo.count})</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="filters-section" style={{ marginBottom: '24px', background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              Transaction Type
            </label>
            <select
              value={filter.type || 'all'}
              onChange={(e) => updateFilter({ type: e.target.value === 'all' ? undefined : e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Types</option>
              <option value="DEPOSIT">Deposits</option>
              <option value="WITHDRAWAL">Withdrawals</option>
              <option value="TRANSFER">Transfers</option>
              <option value="PAYMENT">Payments</option>
              <option value="CREDIT">Credits</option>
              <option value="DEBIT">Debits</option>
              <option value="CRYPTO_BUY">Crypto Buy</option>
              <option value="CRYPTO_SELL">Crypto Sell</option>
              <option value="CRYPTO_TRADE">Crypto Trade</option>
              <option value="STOCK_BUY">Stock Buy</option>
              <option value="STOCK_SELL">Stock Sell</option>
              <option value="OPTION_BUY">Option Buy</option>
              <option value="OPTION_SELL">Option Sell</option>
              <option value="MT5_DEPOSIT">MT5 Deposit</option>
              <option value="MT5_WITHDRAWAL">MT5 Withdrawal</option>
              <option value="MT5_TRADE">MT5 Trade</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              Status
            </label>
            <select
              value={filter.status || 'all'}
              onChange={(e) => updateFilter({ status: e.target.value === 'all' ? undefined : e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="PENDING">Pending</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="PROCESSING">Processing</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              From Date
            </label>
            <input
              type="date"
              value={filter.fromDate || ''}
              onChange={(e) => updateFilter({ fromDate: e.target.value || undefined })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
              To Date
            </label>
            <input
              type="date"
              value={filter.toDate || ''}
              onChange={(e) => updateFilter({ toDate: e.target.value || undefined })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Transaction Table */}
      <div className="admin-table-container">
        <table className="admin-table" key={`table-${activeTab}`}>
          <thead>
            <tr>
              <th onClick={() => handleSort('id')} className="sortable-header">
                Transaction ID {filter.sortBy === 'id' && (filter.sortDirection === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('type')} className="sortable-header">
                Type {filter.sortBy === 'type' && (filter.sortDirection === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('amount')} className="sortable-header">
                Amount {filter.sortBy === 'amount' && (filter.sortDirection === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('customerName')} className="sortable-header">
                Customer {filter.sortBy === 'customerName' && (filter.sortDirection === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('status')} className="sortable-header">
                Status {filter.sortBy === 'status' && (filter.sortDirection === 'ASC' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('createdDate')} className="sortable-header">
                Date {filter.sortBy === 'createdDate' && (filter.sortDirection === 'ASC' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody key={`tbody-${activeTab}`}>
            {(() => {
              return filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    <div style={{ fontSize: '16px', marginBottom: '8px' }}>No transactions found</div>
                    <div style={{ fontSize: '14px' }}>
                      {activeTab === 'all' ? 'Try adjusting your filters or switching to a different tab.' : 
                       `No ${getTabInfo(activeTab).label} transactions found. Try adjusting your filters.`}
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {filteredTransactions.map((transaction) => (
                    <tr key={`${activeTab}-${transaction.id}-${transaction.type}-${transaction.transactionDate}`}>
                      <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {transaction.id}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>{getTransactionTypeIcon(transaction.type)}</span>
                          {transaction.type}
                        </div>
                      </td>
                      <td style={{ 
                        textAlign: 'right', 
                        fontFamily: 'monospace',
                        fontWeight: '600',
                        color: transaction.type === 'WITHDRAWAL' || transaction.type === 'DEBIT' ? '#dc2626' : '#059669'
                      }}>
                        ${transaction.amount.toLocaleString()}
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {transaction.customerName}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            ID: {transaction.customerId}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={getStatusBadgeClass(transaction.status)}>
                          {transaction.status}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px' }}>
                        {formatDate(transaction.transactionDate)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {transaction.status === 'PENDING' && (
                            <>
                              <button
                                className="action-btn success"
                                onClick={() => handleApproveTransaction(transaction.id)}
                                title="Approve Transaction"
                              >
                                ✅ Approve
                              </button>
                              <button
                                className="action-btn danger"
                                onClick={() => handleRejectTransaction(transaction.id)}
                                title="Reject Transaction"
                              >
                                ❌ Reject
                              </button>
                            </>
                          )}
                          
                          <button
                            className="action-btn secondary"
                            onClick={() => openTransactionDetailsModal(transaction)}
                            title="View Details"
                          >
                            👁️ View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              );
            })()}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-info">
            Showing {filteredTransactions.length} of {transactions.length} transactions
            {activeTab !== 'all' && ` (${getTabInfo(activeTab).label} only)`}
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => {
                setCurrentPage(0);
                setFilter(prev => ({ ...prev, page: 0 }));
              }}
              disabled={currentPage === 0}
            >
              First
            </button>
            <button
              className="pagination-btn"
              onClick={() => {
                const newPage = currentPage - 1;
                setCurrentPage(newPage);
                setFilter(prev => ({ ...prev, page: newPage }));
              }}
              disabled={currentPage === 0}
            >
              Previous
            </button>
            <span className="pagination-btn active">{currentPage + 1}</span>
            <button
              className="pagination-btn"
              onClick={() => {
                const newPage = currentPage + 1;
                setCurrentPage(newPage);
                setFilter(prev => ({ ...prev, page: newPage }));
              }}
              disabled={currentPage >= totalPages - 1}
            >
              Next
            </button>
            <button
              className="pagination-btn"
              onClick={() => {
                const lastPage = totalPages - 1;
                setCurrentPage(lastPage);
                setFilter(prev => ({ ...prev, page: lastPage }));
              }}
              disabled={currentPage >= totalPages - 1}
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        isOpen={transactionDetailsModal.isOpen}
        onClose={closeTransactionDetailsModal}
        transaction={transactionDetailsModal.transaction}
      />
    </div>
  );
};

export default TransactionManagement; 